import {
  CallbackProperty,
  Cartesian3,
  defined,
  PolygonHierarchy,
  SceneTransforms,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
} from "@cesium/engine";
import PolygonEntity from "../edit/PolygonEntity";
import DitapAlert from "../popup/DitapAlert";
import Utils from "../basic/Utils";

/**
 * 면 엔티티 생성을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for creating polygon entities using mouse/keyboard input.
 * @alias InteractionPolygonEntity
 * @constructor
 *
 * @param {object} polygonEntityOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [polygonEntityOption.startCallback] 첫 번째 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating first point.
 * @param {Function} [polygonEntityOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point.
 * @param {Function} [polygonEntityOption.endCallback] 마지막 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating last point.
 * @param {PolygonEntity} [polygonEntityOption.PolygonEntity] 면 엔티티 표시 클래스.</br> Polygon entity display class.
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Polygon Entity' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const polygonEntity = new Ditap.PolygonEntity(viewer);
 *
 * // InteractionPolygonEntity on/off 실행 시 PolygonEntity 초기화 필수
 * // PolygonEntity 초기화 방법
 * // 1. InteractionPolygonEntity 생성 후, PolygonEntity와 callback 함수 대입
 * const interactionPolygonEntity = new Ditap.InteractionPolygonEntity();
 * interactionPolygonEntity.polygonEntity = polygonEntity;
 * interactionPolygonEntity.startCallback = () => {};
 * interactionPolygonEntity.addCallback = () => {};
 * interactionPolygonEntity.endCallback = () => {};
 *
 * // 2. InteractionPolygonEntity 생성 시, polygonEntityOption 지정
 * const polygonEntityOption = {
 *    polygonEntity: polygonEntity,
 *    startCallback: () => {}
 *    addCallback: () => {}
 *    endCallback: () => {}
 * };
 *
 * const interactionPolygonEntity = new Ditap.InteractionPolygonEntity(polygonEntityOption);
 */
class InteractionPolygonEntity {
  constructor(polygonEntityOption) {
    this._polygonEntity = undefined;
    if (
      polygonEntityOption &&
      polygonEntityOption.polygonEntity instanceof PolygonEntity
    ) {
      this.polygonEntity = polygonEntityOption.polygonEntity; //원본 클래스
    }
    this._handler = undefined;
    this._isOn = false;
    this._infoLabelEntity = undefined;
    this._mousePosition = undefined;
    this._drawingPointEntities = [];
    this._drawingPolylineEntity = undefined;
    this._windowEvent = {};
    this._warningAlert = undefined;
    this.cancelBtn = undefined; // 핸들러 취소 버튼
    this.removeBtn = undefined; // 한점제거 버튼
    this.endBtn = undefined; // 종료 버튼
    this._isMobile = undefined; // 모바일 여부

    if (polygonEntityOption) {
      if (polygonEntityOption.startCallback instanceof Function) {
        this._startCallback = polygonEntityOption.startCallback;
      } else {
        this._startCallback = undefined;
      }
      if (polygonEntityOption.addCallback instanceof Function) {
        this._addCallback = polygonEntityOption.addCallback;
      } else {
        this._addCallback = undefined;
      }
      if (polygonEntityOption.endCallback instanceof Function) {
        this._endCallback = polygonEntityOption.endCallback;
      } else {
        this._endCallback = undefined;
      }
    }
  }

  /**
   * 면 엔티티 표시 클래스.</br> Polygon entity display class.
   * @type {PolygonEntity}
   */
  set polygonEntity(polygonEntity) {
    if (polygonEntity instanceof PolygonEntity || polygonEntity === undefined) {
      this._polygonEntity = polygonEntity;
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get polygonEntity() {
    return this._polygonEntity;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @readonly
   */
  set isOn(isOn) {
    this._isOn = isOn;
  }
  get isOn() {
    return this._isOn;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 마우스 무브 시 생성된 동적 폴리라인.</br>
   * Dynamic polyline generated on mouse move.
   * @type {Entity}
   */
  set drawingPolylineEntity(drawingPolylineEntity) {
    this._drawingPolylineEntity = drawingPolylineEntity;
  }
  get drawingPolylineEntity() {
    return this._drawingPolylineEntity;
  }

  /*
   * 마우스 무브 시 생성된 동적 포인트 배열.</br>
   * Dynamic point generated on mouse move.
   * @type {Entity[]}
   */
  set drawingPointEntities(drawingPointEntities) {
    this._drawingPointEntities = drawingPointEntities;
  }
  get drawingPointEntities() {
    return this._drawingPointEntities;
  }

  /*
   * 윈도우 이벤트를 포함하는 객체</br>
   * Objects containing Windows events.
   * @type {object[]}
   */
  set windowEvent(windowEvent) {
    this._windowEvent = windowEvent;
  }
  get windowEvent() {
    return this._windowEvent;
  }

  /*
   * 경고 알림 요소.</br>
   * A waring alert.
   * @type {Element}
   */
  set warningAlert(warningAlert) {
    this._warningAlert = warningAlert;
  }
  get warningAlert() {
    return this._warningAlert;
  }

  /**
   * 첫 번째 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating first point
   * @type {Function}
   */
  set startCallback(startCallback) {
    this._startCallback = startCallback;
  }
  get startCallback() {
    return this._startCallback;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point
   * @type {Function}
   */
  set addCallback(addCallback) {
    this._addCallback = addCallback;
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   * 마지막 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating last point
   * @type {Function}
   */
  set endCallback(endCallback) {
    this._endCallback = endCallback;
  }
  get endCallback() {
    return this._endCallback;
  }

  /**
   * 면 생성 핸들러 실행. (PolygonEntity 초기화 필수.)</br>
   * Execute polygon entity creation handler. (PolygonEntity initialization is required.)
   */
  on() {
    if (this.polygonEntity && !this.isOn) {
      this._isMobile = Utils.detectDeviceType();
      this.polygonEntity.viewer.currentEvent = this;
      const viewer = this.polygonEntity.viewer;

      this.handler = new ScreenSpaceEventHandler(viewer.canvas);

      this.isOn = true;
      this.drawingPositions = [];

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.polygonEntity.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여 면을 생성하세요",
        undefined,
        true,
        true
      );

      // LABEL - 설명 라벨 생성
      this.infoLabel = Utils.createInfoLabel(
        this.polygonEntity.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭하여 면을 생성하세요",
        undefined,
        true,
        true
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.endBtn = this.mobileInfoLabel.endBtn;
        this.removeBtn = this.mobileInfoLabel.removeBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.infoLabel.style.display = "none";
        this.createMobileEvent();
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      // 핸들러 - 실행
      this.createLeftClickHandler();
      this.createDoubleClickHandler();
      this.createRightClickHandler();
      this.createWindowEvent();
      this.createResizeEvent();

      // POLYLINE - 동적 엔티티 생성
      const polylineEntity = Utils.createPolyline({
        viewer: viewer,
        positions: new CallbackProperty(() => {
          let arr;
          if (!this._isMobile) {
            let middle = this.mousePosition;
            if (this.drawingPositions.length > 0) {
              const left = this.drawingPositions[
                this.drawingPositions.length - 1
              ];
              const right = this.mousePosition;

              middle = Cartesian3.lerp(left, right, 9 / 10, new Cartesian3());
            }
            arr = [...this.drawingPositions, middle];
          } else {
            arr = [...this.drawingPositions];
          }
          if (arr.length > 1) {
            return arr;
          }
          // if (this.polygonEntity.positions.length > 0) {
          // const positions = this.polygonEntity.positions;
          // const array = [];
          // array.push(positions[positions.length - 1]);
          // array.push(this.mousePosition);
          // return array;
          // }
        }, false),
      });

      this.drawingPolylineEntity = polylineEntity;

      // POLYGON - 동적 엔티티 생성
      const polygonEntity = Utils.createPolygon({
        viewer: viewer,
        positions: new CallbackProperty(() => {
          const arr = [];
          for (const drawingPosition of this.drawingPositions) {
            arr.push(Cartesian3.clone(drawingPosition, new Cartesian3()));
          }

          if (!this._isMobile) {
            arr.push(Cartesian3.clone(this.mousePosition, new Cartesian3()));
          }
          if (arr.length > 2) {
            const positions = new PolygonHierarchy(arr);
            return positions;
          }
          // if (this.polygonEntity.positions.length > 1) {
          //   const newPolygon = JSON.parse(
          //     JSON.stringify(this.polygonEntity.positions)
          //   );
          //   newPolygon.push(this.mousePosition);
          //   const polygon = new PolygonHierarchy(newPolygon);
          //   return polygon;
          // }
        }, false),
      });
      this.drawingPolygonEntity = polygonEntity;

      // 경고창 생성
      this.warningAlert = new DitapAlert({
        type: "warning",
        title: "경고",
        content1: "면 생성 시, ",
        content2: "세 개 이상의 점이 필요합니다",
        buttons: "ONLYOK",
        open: false,
        callback: () => {},
      });
    }
  }

  /**
   * 면 생성 핸들러 종료. (PolygonEntity 초기화 필수.)</br>
   * End polygon measurement handler. (PolygonEntity initialization is required.)
   */
  off() {
    if (this.polygonEntity && this.isOn) {
      const viewer = this.polygonEntity.viewer;

      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      // POINT 삭제
      this.drawingPointEntities.forEach((pointEntity) => {
        viewer.entities.remove(pointEntity);
      });

      viewer.entities.remove(this.drawingPolylineEntity);
      viewer.entities.remove(this.drawingPolygonEntity);

      if (this.polylinePrimitive) {
        viewer.scene.primitives.remove(this.polylinePrimitive);
      }

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
        window.removeEventListener("keydown", this.onKeyDown);

        if (this.resizeEvent) {
          window.removeEventListener("resize", this.resizeEvent);
        }
        this.removeWindowEvent();
      }

      if (this._isMobile) {
        this.removeMobileEvent();
      }

      // 프로퍼티 초기화
      this.handler = undefined;
      this.drawingPositions = [];
      this.isOn = false;
      this.mousePosition = undefined;
      this.drawingPolylineEntity = undefined;
      this.drawingPointEntities = [];
      this.windowEvent = {};
      this.infoLabelEntity = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;

      // alert창 삭제
      this.warningAlert.destroy();
      this.warningAlert = undefined;
      this.polygonEntity.viewer.currentEvent = undefined;
      this.polygonEntity = undefined;
      this.cancelBtn = undefined;
      this.endBtn = undefined;
      this.removeBtn = undefined;
    }
  }
  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.endBtn = this.mobileInfoLabel.endBtn;
            this.removeBtn = this.mobileInfoLabel.removeBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);

            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.destroy();
      })
    );

    this.endBtn.addEventListener(
      "click",
      (this.endEvent = () => {
        this.endHandler();
      })
    );

    this.removeBtn.addEventListener(
      "click",
      (this.removeEvent = () => {
        this.removePoint();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this.cancelEvent) {
      this.cancelBtn.removeEventListener("click", this.cancelEvent);
      this.cancelBtn = undefined;
      this.cancelEvent = undefined;
    }
    if (this.endEvent) {
      this.endBtn.removeEventListener("click", this.endEvent);
      this.endBtn = undefined;
      this.endEvent = undefined;
    }
    if (this.removeEvent) {
      this.removeBtn.removeEventListener("click", this.removeEvent);
      this.removeBtn = undefined;
      this.removeEvent = undefined;
    }
  }
  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const handler = this.handler;

    // LEFT_CLICK 핸들러
    handler.setInputAction((e) => {
      if (this.isOn) {
        const allDynamicEntity = [
          ...this.drawingPointEntities,
          this.drawingPolygonEntity,
          this.drawingPolylineEntity,
        ];

        allDynamicEntity.forEach((entities) => {
          entities.show = false;
        });

        setTimeout(() => {
          const position = Utils.pickPosition(
            e.position,
            this.polygonEntity.viewer
          );

          if (defined(position)) {
            const viewer = this.polygonEntity.viewer;
            let isEqual = false;
            this.mousePosition = position;

            // 마지막으로 추가된 좌표와 현재 좌표 비교
            const drawingPositionsLength = this.drawingPositions.length;
            if (drawingPositionsLength > 0) {
              const lastPosition = this.drawingPositions[
                drawingPositionsLength - 1
              ];
              if (lastPosition.equals(position)) {
                isEqual = true;
              }
            }

            // 영역 지정 포인트 생성
            if (!isEqual && defined(position)) {
              this.polygonEntity.positions.push(position);
              const drawingPoint = Utils.createPoint({
                viewer: viewer,
                position: position,
              });
              this.drawingPointEntities.push(drawingPoint);

              // 영역 지정중인 좌표 추가
              this.drawingPositions.push(position);

              // 점 생성 시 콜백함수 실행
              if (this.addCallback && this.polygonEntity) {
                this.addCallback(this.polygonEntity);
              }

              if (
                this.drawingPositions.length === 1 &&
                this.startCallback &&
                this.polygonEntity
              ) {
                this.startCallback(this.polygonEntity);
              }
            }
          }

          allDynamicEntity.forEach((entities) => {
            entities.show = true;
          });
        }, 5);
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.polygonEntity.viewer;
    const handler = this.handler;
    handler.setInputAction((e) => {
      if (this.isOn) {
        const position = Utils.pickPosition(e.endPosition, viewer);
        if (defined(position)) {
          this.mousePosition = position;
          // 도움말 라벨 좌표 이동
          Utils.changeLabelLocation(
            viewer,
            this.mousePosition,
            this.infoLabelEntity
          );

          if (this.polylinePrimitive) {
            viewer.scene.primitives.remove(this.polylinePrimitive);
          }

          const positions = this.drawingPositions;

          if (positions.length > 0) {
            const array = [];
            array.push(positions[positions.length - 1]);
            array.push(position);
            const polyline = Utils.createPolylinePrimitive({
              viewer: viewer,
              positions: array,
            });
            this.polylinePrimitive = polyline;
          }
        } else {
          this.infoLabelEntity.classList.add("hide");
        }
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /*
   * 마우스 더블 클릭 이벤트 핸들러 생성.</br>
   * Create mouse double-click event handler.
   */
  createDoubleClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.endHandler();
    }, ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
  }

  /*
   * 면생성 종료.<br>
   * End straight line distance measurement.
   */
  endHandler() {
    const positions = this.polygonEntity.positions;
    if (this.isOn) {
      if (positions.length > 2) {
        setTimeout(() => {
          this.polygonEntity.createEntity();

          // 마지막 점 생성 시 콜백함수 실행
          if (this.endCallback && this.polygonEntity) {
            this.endCallback(this.polygonEntity);
          }

          this.off();
        }, 6);
      } else {
        this.warningAlert.open();
      }
    }
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      if (this.isOn) {
        if (this.drawingPositions.length > 0) {
          this.removePoint();
        } else {
          this.abort();
          this.off();
        }
      }
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // ESC 클릭 : Entity 제거, 모든 핸들러 종료
      this.off();
      this.abort();
    } else if (event.key === "Backspace") {
      this.removePoint();
    }
  }

  /**
   *  생성 중 중단.</br>
   * Interrupt during measurement.
   */
  abort() {
    // viewerStorage에서 삭제
    this.polygonEntity?.viewer.ditapStorage.removeInstanceById(
      this.polygonEntity.id
    );

    //polygonEntity 삭제
    this.polygonEntity?.destroy();
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /*
   * 생성된 마지막 엔티티 삭제.</br>
   * Remove the last entity created.
   */
  removePoint() {
    const viewer = this.polygonEntity.viewer;
    // 마지막 좌표 제거
    // 영역 지정 마지막 좌표 제거
    if (this.polylinePrimitive) {
      viewer.scene.primitives.remove(this.polylinePrimitive);
    }

    const drawingPositionsLength = this.drawingPositions.length;
    if (drawingPositionsLength > 0) {
      // 배열 마지막 아이템 제거
      this.drawingPositions = this.drawingPositions.slice(
        0,
        drawingPositionsLength - 1
      );

      this.polygonEntity.positions.pop();
    }
    const drawingPointEntityLength = this.drawingPointEntities.length;
    if (drawingPointEntityLength > 0) {
      const lastPointEntity = this.drawingPointEntities[
        drawingPointEntityLength - 1
      ];
      // 엔티티 제거
      viewer.entities.remove(lastPointEntity);
      // 배열 마지막 아이템 제거
      this.drawingPointEntities = this.drawingPointEntities.slice(
        0,
        drawingPointEntityLength - 1
      );
    }
  }

  /*
   * WGS84 좌표를 창 좌표로 변환하여 레이블 위치 변경.</br>
   * Change the position of the label by converting the position of the WGS84 coordinates into window coordinates.
   */
  labelLocationWindowEvent() {
    const viewer = this.polygonEntity.viewer;
    const label = this.infoLabelEntity;

    if (viewer && label && this.mousePosition) {
      // cartesian -> window좌표로 변환
      const windowPosition = SceneTransforms.wgs84ToWindowCoordinates(
        viewer.scene,
        this.mousePosition
      );

      if (windowPosition) {
        // ditapContainer의 width, height보다 큰 경우는 라벨 안보이게
        if (
          viewer.container.offsetWidth > windowPosition.x &&
          viewer.container.offsetHeight > windowPosition.y &&
          windowPosition.x > 0 &&
          windowPosition.y > 0
        ) {
          label.style.left = `${windowPosition.x + 10}px`;
          label.style.top = `${windowPosition.y + 10}px`;
        }
      }
    }
  }

  /*
   * 윈도우 객체에 추가된 이벤트 제거.</br>
   * Remove Events added on Windows Objects
   */
  removeWindowEvent() {
    const keys = Object.keys(this.windowEvent);
    for (let i = 0; i < keys.length; i++) {
      window.removeEventListener(keys[i], this.windowEvent[keys[i]]);
    }
  }

  /*
   * 윈도우 객체에 이벤트 추가.</br>
   * Add events to Window object
   */
  createWindowEvent() {
    // keyDown 이벤트 생성
    window.addEventListener(
      "keydown",
      (this.windowEvent["keydown"] = (e) => {
        this.onKeyDown(e);
      })
    );
  }
}
export default InteractionPolygonEntity;
